import React, { useEffect, useRef, useState } from "react";
import type { FroalaOptions } from "froala-editor";
import { toast } from "react-toastify";
import { loadDuoEditor } from "./loadDuoEditor";

type DuoEditorElement = HTMLElement & {
  config?: Partial<FroalaOptions>;
  value: string;
  updateContent(html: string): void;
};

interface DuoEditorProps {
  value: string;
  onChange: (html: string) => void;
  config: Partial<FroalaOptions>;
}

const DuoEditor: React.FC<DuoEditorProps> = ({ value, onChange, config }) => {
  const elementRef = useRef<DuoEditorElement | null>(null);
  const [isReady, setIsReady] = useState(false);
  const lastEmitted = useRef("");

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    loadDuoEditor().catch(() => toast.error("Error loading the text editor"));
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }
    const handleChange = (event: Event) => {
      if (event.target !== element || !(event instanceof CustomEvent)) {
        return;
      }
      const html = String(event.detail ?? "");
      lastEmitted.current = html;
      onChangeRef.current(html);
    };
    const handleInitialized = () => queueMicrotask(() => setIsReady(true));

    element.addEventListener("change", handleChange);
    element.addEventListener("initialized", handleInitialized);
    return () => {
      element.removeEventListener("change", handleChange);
      element.removeEventListener("initialized", handleInitialized);
    };
  }, []);

  useEffect(() => {
    if (elementRef.current) {
      elementRef.current.config = config;
    }
  }, [config]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !isReady || value === lastEmitted.current) {
      return;
    }
    if (element.value !== value) {
      lastEmitted.current = value;
      element.updateContent(value);
    }
  }, [value, isReady]);

  return <duo-editor ref={elementRef} />;
};

export default DuoEditor;
